/* ========================================================= * Material Kit 2 PRO React - v2.0.0 ========================================================= * Product Page: https://www.creative-tim.com/product/material-kit-pro-react * Copyright 2021 Creative Tim (https://www.creative-tim.com) Coded by www.creative-tim.com ========================================================= * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. */

import { forwardRef, useState } from "react";
import { useMaterialUIController } from "/context";
import dynamic from "next/dynamic";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from 'next/router';
import findFeatures, { findFeatureByFeaturePath } from '/pages/api/features';
import { robotoSlab } from "/utils/fonts";
import Slide from "@mui/material/Slide";
import config from "/config";
import DialogPictureDisplay from "/examples/DialogPictureDisplay";
import DialogVidDisplay from "/examples/DialogVidDisplay";
import DialogAboutDisplay from "/examples/DialogAboutDisplay";

const AppBar = dynamic(() => import("@mui/material/AppBar"), { loading: () => null });
const Card = dynamic(() => import("@mui/material/Card"), { loading: () => null });
const Container = dynamic(() => import("@mui/material/Container"), { loading: () => null });
const Dialog = dynamic(() => import("@mui/material/Dialog"), { loading: () => null });
const DialogActions = dynamic(() => import("@mui/material/DialogActions"), { loading: () => null });
const DialogContent = dynamic(() => import("@mui/material/DialogContent"), { loading: () => null });
const Grid = dynamic(() => import("@mui/material/Grid"), { loading: () => null });
const IconButton = dynamic(() => import("@mui/material/IconButton"), { loading: () => null });
const Tabs = dynamic(() => import("@mui/material/Tabs"), { loading: () => null });
const Tab = dynamic(() => import("@mui/material/Tab"), { loading: () => null });
const Toolbar = dynamic(() => import("@mui/material/Toolbar"), { loading: () => null });
const AppsRounded = dynamic(() => import("@mui/icons-material/AppsRounded"), { loading: () => null });
const Close = dynamic(() => import("@mui/icons-material/Close"), { loading: () => null });
const PhoneIphoneRounded = dynamic(() => import("@mui/icons-material/PhoneIphoneRounded"), { loading: () => null });
const SquareRounded = dynamic(() => import("@mui/icons-material/SquareRounded"), { loading: () => null });
const WindowRounded = dynamic(() => import("@mui/icons-material/WindowRounded"), { loading: () => null });
const MDBox = dynamic(() => import('/components/MDBox'), { loading: () => null });
const MDButton = dynamic(() => import('/components/MDButton'), { loading: () => null });
const MDTypography = dynamic(() => import('/components/MDTypography'), { loading: () => null });
const ALLDVideo = dynamic(() => import('/examples/ALLDVideo'), { loading: () => null });
const GoogleAnalytics = dynamic(() => import('/examples/GoogleAnalytics'), { loading: () => null });

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function EntryPage({ entry, photos, photos3d, videos, about, photosBefore, photosDuring, entries, prevRoute }) {
  const [controller] = useMaterialUIController();
  const router = useRouter();
  const [openDialog, setOpenDialog] = useState(true);
  const [tab, setTab] = useState("photos");
  const [viewMode, setViewMode] = useState(4);

  return entry.length !== 0 && (
    <>
      <Head>
        <title key="title">{`${entry.name} by Autumn Leaf`}</title>
        <meta name="description" content={`Check out our ${entry.name}: ${entry.description}`} />
        <link rel="canonical" href={`${config.frontURL}/our-work/feature/${entry.path}`} />
        <meta property="og:site_name" content="Autumn Leaf Landscape" />
        <meta property="og:url" content={`${config.frontURL}/our-work/feature/${entry.path}`} />
        <meta property="og:title" content={entry.name} />
        <meta property="og:description" content={entry.description} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content={`${config.frontURL}/feature/${entry.id}/facebook.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="photo" />
        <meta name="twitter:title" content={entry.name} />
        <meta name="twitter:description" content={entry.description} />
        <meta name="twitter:url" content={`${config.frontURL}/our-work/feature/${entry.path}`} />
        <meta name="twitter:image" content={`${config.frontURL}/features/${entry.id}/twitter.jpg`} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": `${config.frontURL}/our-work/feature/${entry.path}`
              },
              "headline": entry.name,
              "description": entry.description,
              "image": `${config.frontURL}/${entry.bannerImagePath}`,
              "author": {
                "@type": "Organization",
                "name": "Autumn Leaf Landscape Design",
                "url": config.frontURL,
              },
              "publisher": {
                "@type": "Organization",
                "name": "Autumn Leaf Landscape Design",
                "logo": {
                  "@type": "ImageObject",
                  "url": `${config.frontURL}/logo.png`
                }
              },
              "datePublished": null,
              "dateModified": null,
              "image": {
                "@type": "ImageObject",
                "@id": `${config.frontURL}/${entry.bannerImagePath}`,
                "url": `${config.frontURL}/${entry.bannerImagePath}`,
                "height": entry.bannerImageHeight,
                "width": entry.bannerImageWidth
              },
              "url": `${config.frontURL}/our-work/feature/${entry.path}`,
              "isPartOf": {
                "@type": "Blog",
                "@id": `${config.frontURL}/our-work`,
                "name": "Autumn Leaf Project Blog",
                "publisher": {
                  "@type": "Organization",
                  "@id": config.frontURL,
                  "name": "Autumn Leaf Landscape Design"
                }
              },
              "commentCount": "0",
              "about": [
                {
                  "@type": "Thing",
                  "@id": `${config.frontURL}/our-work/feature/${entry.path}`,
                  "name": `${config.frontURL}/our-work/feature/${entry.path}`,
                },
              ],
              "keywords": [
                entry.name,
              ]
            })
          }}
        />
      </Head>
      <GoogleAnalytics />
      <Dialog
        fullScreen
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          if (prevRoute !== undefined && !prevRoute.includes("our-work/feature/")) {
            router.back();
          }
          else router.push('/our-work');
        }}
        PaperProps={{ style: { backgroundColor: "transparent", overflowY: "visible" } }}
        TransitionComponent={Transition}
      >
        <Card sx={{ overflow: "scroll", borderRadius: "initial" }}>
          <AppBar sx={{ position: 'relative' }}>
            <Toolbar>
              <Link href="/our-work">
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={() => {
                    setOpenDialog(false);
                    if (prevRoute !== undefined && !prevRoute.includes("our-work/feature/")) {
                      router.back();
                    }
                    else router.push('/our-work');
                  }}
                  aria-label="close"
                >
                  <Close color={controller.darkMode ? "white" : "dark"} />
                </IconButton>
              </Link>
              <MDTypography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                Autumn Leaf{" "}
                {controller.mobileView &&
                  <MDButton
                    href="tel:1-(631)-424-5544"
                    iconOnly
                    color="dark"
                    sx={{ borderRadius: 18, marginLeft: 1 }}
                    ml={2}
                  >
                    <PhoneIphoneRounded />
                  </MDButton>
                }
                {!controller.mobileView &&
                  <MDButton
                    color="info"
                    href="tel:1-(631)-424-5544"
                    variant="contained"
                    size="small"
                    sx={{ marginLeft: 1 }}
                  >
                    (631) 424-5544
                  </MDButton>
                }
              </MDTypography>
              <MDBox display="flex" sx={{ alignItems: "center" }}>
                {!controller.mobileView && (
                  <>
                    <MDBox mr={2}>
                      <MDButton variant={controller.darkMode ? "text" : "contained"} color={controller.darkMode ? "dark" : "white"} iconOnly onClick={() => setViewMode(4)} aria-label="3 Columns">
                        <AppsRounded color={viewMode === 4 ? "info" : "default"} />
                      </MDButton>
                    </MDBox>
                    <MDBox mr={2}>
                      <MDButton variant={controller.darkMode ? "text" : "contained"} color={controller.darkMode ? "dark" : "white"} iconOnly onClick={() => setViewMode(6)} aria-label="2 Columns">
                        <WindowRounded color={viewMode === 6 ? "info" : "default"} />
                      </MDButton>
                    </MDBox>
                    <MDBox mr={2}>
                      <MDButton variant={controller.darkMode ? "text" : "contained"} color={controller.darkMode ? "dark" : "white"} iconOnly onClick={() => setViewMode(12)} aria-label="1 Column">
                        <SquareRounded color={viewMode === 12 ? "info" : "default"} />
                      </MDButton>
                    </MDBox>
                  </>
                )}
              </MDBox>
            </Toolbar>
          </AppBar>
          {entry.length !== 0 && (
            <>
              <DialogContent sx={{ paddingTop: 0 }}>
                <MDBox component="section" py={{ xs: 0, lg: 0 }}>
                  <MDBox
                    borderRadius="xl"
                    shadow="lg"
                    sx={{ position: "relative", overflow: "hidden" }}>
                    <Image
                      src={entry.bannerImagePath}
                      alt=""
                      width={0}
                      height={0}
                      sizes="96vw"
                      style={{ objectFit: "cover", width: '100%', height: '100%', zIndex: 0, top: 0, left: 0, position: "absolute" }}
                      priority
                    />
                    <MDBox
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundImage: ({ palette: { gradients }, functions: { linearGradient, rgba } }) =>
                          `${linearGradient(
                            rgba(gradients.dark.main, 0.6),
                            rgba(gradients.dark.state, 0.6)
                          )}`,
                        backgroundSize: "cover",
                      }}
                    />
                    <Container sx={{ position: "relative", pb: { xs: 20, lg: 22 }, pt: 12 }}>
                      <Grid
                        container
                        item
                        flexDirection="column"
                        alignItems="center"
                        xs={12}
                        md={8}
                        sx={{ mx: "auto", textAlign: "center" }}
                      >
                        <MDTypography
                          variant="caption"
                          textTransform="uppercase"
                          fontWeight="medium"
                          sx={{ display: "block" }}
                        >
                          <span style={{ color: controller.darkMode ? entry.darkColor : entry.lightColor }}>{entry.projectType}</span>
                        </MDTypography>
                        <MDTypography variant="h3" color="white" mb={2} className={robotoSlab.className}>
                          {entry.name}
                        </MDTypography>
                        <MDTypography variant="body2" color="white">
                          {entry.description}
                        </MDTypography>
                      </Grid>
                    </Container>
                  </MDBox>
                  <MDBox mt={-16}>
                    <Container>
                      <Grid container sx={{ mb: 6 }}>
                        <Grid item xs={7} md={6} lg={4} sx={{ mx: "auto", textAlign: "center" }}>
                          {entry.length !== 0 && (
                            <AppBar position="static">
                              <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)}>
                                {photos.filter((obj) => obj.type === "photo").length !== 0 && <Tab value="photos" label={<MDBox py={0.5} px={2} color="inherit">Photos</MDBox>} aria-label="Photos" />}
                                {photos3d.filter((obj) => obj.type === "3d").length !== 0 && <Tab value="3d" label={<MDBox py={0.5} px={2} color="inherit">3D</MDBox>} aria-label="3D Design" />}
                                {videos.length !== 0 && <Tab value="vids" label={<MDBox py={0.5} px={2} color="inherit">Videos</MDBox>} aria-label="Videos" />}
                                {about.length !== 0 && <Tab value="about" label={<MDBox py={0.5} px={2} color="inherit">About</MDBox>} aria-label="About" />}
                                {photosDuring.length !== 0 && <Tab value="during" label={<MDBox py={0.5} px={2} color="inherit">During</MDBox>} aria-label="During Photos" />}
                                {photosBefore.filter((obj) => obj.type === "before").length !== 0 && <Tab value="before" label={<MDBox py={0.5} px={2} color="inherit">Before</MDBox>} aria-label="Before Photos" />}
                              </Tabs>
                            </AppBar>
                          )}
                        </Grid>
                      </Grid>
                      {tab === "photos" && <DialogPictureDisplay array={photos} keyPrefix="photos" viewMode={viewMode} />}
                      {tab === "3d" && <DialogPictureDisplay array={photos3d} keyPrefix="photos3d" viewMode={viewMode} />}
                      {tab === "vids" && <DialogVidDisplay array={videos} keyPrefix="videos-" viewMode={viewMode} />}
                      {tab === "during" && <DialogPictureDisplay array={photosDuring} keyPrefix="photosDuring" viewMode={viewMode} />}
                      {tab === "before" && <DialogPictureDisplay array={photosBefore} keyPrefix="photosBefore" viewMode={viewMode} />}
                      {tab === "about" && <DialogAboutDisplay array={about} keyPrefix="about" />}
                    </Container>
                  </MDBox>
                </MDBox>
              </DialogContent>
              <DialogActions>
                <MDButton
                  color={controller.darkMode ? "dark" : "white"}
                  disabled={entry.id === 1}
                  onClick={() => {
                    setTab("photos");
                    setViewMode(4);
                    if (feature.id !== 1) {
                      router.push(entries.find((obj) => obj.id === entry.id - 1).path);
                    } else {
                      router.push(entries.find((obj) => obj.id === entries.length).path)
                    }
                  }}>Previous Feature</MDButton>
                <MDButton
                  color={controller.darkMode ? "dark" : "white"}
                  disabled={entry.id === entries.length}
                  onClick={() => {
                    setTab("photos");
                    setViewMode(4);
                    if (entry.id !== entries.length) {
                      router.push(entries.find((obj) => obj.id === entry.id + 1).path)
                    } else {
                      router.push(entries.find((obj) => obj.id === 0).path)
                    }
                  }} autoFocus>
                  Next Feature
                </MDButton>
              </DialogActions>
            </>
          )}
        </Card>
      </Dialog>
    </>
  );
}

export async function getStaticPaths() {
  const entries = await findFeatures();
  const paths = entries.map((entry) => ({
    params: { path: entry.path },
  }));
  return { paths, fallback: false };
}

export async function getStaticProps({ params }) {
  const entry = findFeatureByFeaturePath(params.path);
  const entries = findFeatures();
  const photos = entry.pics.filter((obj) => obj.type === "photo");
  const photos3d = entry.pics.filter((obj) => obj.type === "3d");
  const videos = entry.vids;
  const about = entry.about;
  const photosBefore = entry.pics.filter((obj) => obj.type === "before");
  const photosDuring = entry.pics.filter((obj) => obj.type === "during");
  return { props: { entry, photos, photos3d, videos, about, photosBefore, photosDuring, entries } };
}
